import React, { createContext, useReducer } from 'react';
import { isSmallDevice, router } from './helpers';

const snackbarDefault = { isOpen: false, message: null, variant: 'info' };

const updateNavTree = (navTree, userOptions = {}) => {
  const isNavLinkVisible = link => !link.hideNavLink && (!link.requiresOption || !!userOptions[link.requiresOption]);

  const sidenavLinks = navTree.filter(isNavLinkVisible);
  const sidenavLinksState = navTree.filter(isNavLinkVisible).reduce((acc, cur) => {
    if (cur.children) {
      acc[cur.route.replace('/', '')] = false;
    }
    return acc;
  }, {});

  return {
    navTree,
    sidenavLinks: sidenavLinks,
    sidenavLinksState: sidenavLinksState,
  };
};

// recursive reducer to flatten nested arrays of routes into unique route objects
const flattenRouter = (routeArr, path = '') => {
  return routeArr.reduce((acc, val) => {
    val.path = `${path}${val.route}`;
    if (val.children) {
      acc = acc.concat(flattenRouter(val.children, val.path));
    } else {
      acc = acc.concat(val);
    }
    return acc;
  }, []);
};

/**
 * @func reduceRoutes
 * @desc filter out any routes that do not belong to this roles
 * @param { Array[ object ] } routes
 * @param { Array[ string ] } roles
 * @param { number } childDepth   // in case we ever need to do something starting at a certain depth
 */
const reduceRoutes = (routes, roles, childDepth = 0) => {
  return routes.reduce((allowedRoutes, route) => {
    // if no roles are allowed, do not include this route
    if (route.permissions && !route.permissions.some(permission => roles.indexOf(permission) >= 0)) {
      return allowedRoutes;
    }

    // reduce children if available
    if (route.children) {
      route.children = reduceRoutes(route.children, roles, childDepth + 1);
    }

    // allow the route for this list of roles
    allowedRoutes.push(route);

    return allowedRoutes;
  }, []);
};

const updateRoles = (roles = [], userOptions = {}) => {
  // const newRoutes = router.filter(route => !route.permissions || route.permissions.indexOf(role) !== -1);
  const filteredRoutes = reduceRoutes(router, roles);
  const flatRouter = flattenRouter(filteredRoutes);
  const navTree = updateNavTree(filteredRoutes, userOptions);
  return {
    roles,
    flatRouter,
    ...navTree,
  };
};

let reducer = (state, action) => {
  switch (action.type) {
    case 'SET_SIDEBAR_IS_OPEN':
      return { ...state, sidebarIsOpen: action.sidebarIsOpen };

    // -----------------------

    case 'SET_ACCOUNT_DROPDOWN_IS_OPEN':
      return { ...state, accountDropdownIsOpen: action.accountDropdownIsOpen };

    // -----------------------

    case 'UPDATE_PAGE_TITLE':
      return { ...state, pageTitle: action.pageTitle };

    // -----------------------

    case 'UPDATE_NAV_TREE':
      return {
        ...state,
        ...updateNavTree(action.navTree),
      };

    case 'UPDATE_SIDENAV_LINKS':
      return {
        ...state,
        sidenavLinks: action.sidenavLinks,
        sidenavLinksState: action.sidenavLinks.reduce((acc, cur) => {
          if (cur.children) {
            acc[cur.route.replace('/', '')] = false;
          }
          return acc;
        }, {}),
      };
    case 'UPDATE_SIDENAV_LINKS_STATE':
      return {
        ...state,
        sidenavLinksState: action.sidenavLinksState,
      };

    // -----------------------

    case 'UPDATE_ROLES':
      const temp = updateRoles(action.roles, { canProcessCancel: action.canProcessCancel });

      const existingOpenSidenav = Object.keys(state.sidenavLinksState).find(key => state.sidenavLinksState[key]);

      if (existingOpenSidenav) {
        temp.sidenavLinksState[existingOpenSidenav] = true;
      }

      return {
        ...state,
        ...temp,
      };

    // -----------------------

    case 'UPDATE_ACCOUNT_INFO':
      return {
        ...state,
        ...action,
      };

    // -----------------------

    case 'SET_STATE':
      const { type, ...sanitized } = action;
      return {
        ...state,
        ...sanitized,
      };

    // -----------------------
    case 'SET_SNACKBAR':
      return {
        ...state,
        snackbar: {
          ...snackbarDefault,
          ...action.snackbar,
        },
      };

    // -----------------------

    case 'SET_INITIAL_APP_LOAD_COMPLETE':
      return {
        ...state,
        initialAppLoadComplete: action.initialAppLoadComplete,
      };
    //   case 'SET_PERMISSIONS':
    //   return { ...state, permissions: action.permissions };
    //   case 'UPDATE_PROFILE':
    //   return { ...state, profile: action.profile};
    // case 'UPDATE_RESTRICTIONS':
    //   return { ...state, restrictions: action.restrictions };

    default:
      return state;
  }
};

const initialState = {
  sidebarIsOpen: window.innerWidth > 768,
  accountDropdownIsOpen: false,

  navTree: [],
  sidenavLinks: [],
  sidenavLinksState: {},
  flatRouter: [],

  routes: [],
  pageTitle: '',
  accountName: 'Account',
  roles: [],
  snackbar: snackbarDefault,

  // account info
  userId: null,
  username: null,
  email: null,
  phoneNumber: null,
  canProcessCancel: false,
  initialAppLoadComplete: false,
};

export const AppContext = createContext(initialState);

export const AppContextProvider = props => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return <AppContext.Provider value={{ state, dispatch }}>{props.children}</AppContext.Provider>;
};
